import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

// Add import for PlayerPool
import PlayerPool from './PlayerPool';

// Add import for AnimationController
import AnimationController from './AnimationController';

// Create a simple markRaw function that returns the object unchanged
// This allows the code to work even if Vue is not available
const markRaw = (obj) => obj;

// Constants for player positioning
const TERRAIN_Y_OFFSET = 0.05; // Height offset from terrain
const PLAYER_EYE_HEIGHT = 1.6; // Height of camera above player position

class PlayerManager {
  constructor(scene, camera, mapScene) {
    this.scene = scene || markRaw(new THREE.Scene());
    this.camera = camera;
    this.mapScene = mapScene;
    this.players = {}; // Object to store other players by their socket ID
    this.localPlayer = null; // The player's own mesh
    this.gltfLoader = markRaw(new GLTFLoader()); // GLTF loader for 3D models
    this.modelCache = {}; // Cache for loaded models
    this.characterModels = {}; // Store character model information
    
    // Add player pools
    this.playerPools = {};
    
    // Weapon models cache
    this.weaponModels = {};
    
    // Debug options
    this.showHitboxes = false; // Toggle for hitbox visualization
    this.hitboxHelpers = {}; // Storage for hitbox visualization helpers
    
    // Effects tracking
    this.spawnProtectionOverlay = null;
    this.spawnProtectionTimer = null;
    
    // Interpolation settings
    this.interpolationFactor = 0.2; // Lower = smoother but more laggy, higher = less lag but jerkier
    this.positionBuffer = {}; // Store target positions for interpolation
    
    // Animation blending settings
    this.useAnimationBlending = false; // Disabled animation blending
    this.defaultBlendDuration = 0.3; // Default time in seconds for animation crossfade
    this.directionalBlendDuration = 0.5; // Longer blend time for directional changes
    this.runningBlendDuration = 0.2; // Shorter blend time for running transitions
    this.idleBlendDuration = 0.2; // Longer blend time when transitioning to idle
    this.animationTransitions = {}; // Track ongoing transitions for players
    
    // Add the AnimationController
    this.animationController = new AnimationController();
    this.animationController.setDebug(true); // Enable debug mode during development
    
    this.setupCharacterModels(); // Initialize character models
    
    // Initialize the soldier model pool
    this.initializePlayerPools();
    
    // Preload weapon models
    this.preloadWeaponModels();
    
    // Add this near the top of the class with other properties
    this.lastAnimationChange = {};
    this.animationDebounceTime = 100; // ms
    
    // Add movement tracking properties
    this.movementStates = {};
    this.movementThreshold = 0.05;
    this.movementGracePeriod = 200; // ms
    
    // Add animation state tracking
    this.animationStates = {};
    this.animationLockTime = 300; // ms
  }
  
  // Add this new method to initialize player pools
  initializePlayerPools() {
    // Get the path for the soldier model
    const soldierConfig = this.characterModels.soldier;
    if (!soldierConfig || !soldierConfig.modelPath) {
      console.warn('Could not find soldier model configuration');
      return;
    }
    
    // Create a pool for soldier models with a reference to this PlayerManager instance
    this.playerPools.soldier = new PlayerPool(this.scene, soldierConfig.modelPath, this);
    
    // Initialize the pool with the character config (async)
    this.playerPools.soldier.initialize(soldierConfig).then(success => {
      if (success) {
        console.log('Soldier model pool initialized successfully');
        
        // Notify any component waiting for pool initialization
        const poolInitEvent = new CustomEvent('pool-initialized', {
          detail: { type: 'soldier', success: true }
        });
        window.dispatchEvent(poolInitEvent);
      } else {
        console.warn('Failed to initialize soldier model pool');
        
        // Notify any component waiting for pool initialization
        const poolInitEvent = new CustomEvent('pool-initialized', {
          detail: { type: 'soldier', success: false }
        });
        window.dispatchEvent(poolInitEvent);
      }
    });
  }
  
  // Helper method to properly use pool or regular model creation
  shouldUsePoolForPlayer(characterType, isLocalPlayer) {
    // Normalize character type
    const normalizedType = characterType.toLowerCase();
    
    // Always use pool for all players if a pool exists for their character type
    // Whether local player or not, frontend or test client
    return (
      this.playerPools[normalizedType] &&
      this.playerPools[normalizedType].isInitialized
    );
  }
  
  // Preload weapon models
  preloadWeaponModels() {
    // Define weapon models to load with PUBLIC path
    const weaponModelPaths = {
      rifle: './models/ak.glb' // Using relative path from public directory
    };
    
    console.log("========= PRELOADING WEAPON MODELS =========");
    console.log("Weapon model paths:", weaponModelPaths);
    
    // Create a placeholder weapon model in case file loading fails
    this.createPlaceholderWeaponModels();
    
    // Try checking if the file exists with an image (won't actually work for models)
    Object.entries(weaponModelPaths).forEach(([type, path]) => {
      const img = new Image();
      img.onload = () => console.log(`🟢 Confirmed file exists at path: ${path}`);
      img.onerror = () => {
        console.warn(`🔶 File not found at path: ${path} - using placeholder model`);
        // Already using placeholder, so no further action needed
      };
      img.src = path;
    });
    
    // Load each weapon model
    const loadPromises = Object.entries(weaponModelPaths).map(([weaponType, modelPath]) => {
      console.log(`Attempting to load ${weaponType} model from path: ${modelPath}`);
      
      return this.loadModel(modelPath).then(model => {
        //console.log(`✅ SUCCESS: Loaded ${weaponType} model`);
        this.weaponModels[weaponType] = model;
        
        if (!model.scene) {
          console.error(`⚠️ Model loaded but has no scene: ${weaponType}`);
        } else {
       //   console.log(`🟢 Model has valid scene with ${model.scene.children.length} children`);
          
          // Count meshes
          let meshCount = 0;
          model.scene.traverse(obj => {
            if (obj.isMesh) meshCount++;
          });
        //  console.log(`🟢 Model has ${meshCount} meshes`);
        }
        
        return { type: weaponType, success: true };
      }).catch(error => {
        console.warn(`⚠️ FAILED to load weapon model ${weaponType}, using placeholder:`, error);
        // Use the placeholder model we created earlier
        this.weaponModels[weaponType] = this.placeholderModels[weaponType];
        return { type: weaponType, success: true, usedPlaceholder: true };
      });
    });
    
    // Log when all weapon models are loaded
    Promise.all(loadPromises)
      .then(results => {
        const loadedWeapons = results.filter(r => r.success && !r.usedPlaceholder).map(r => r.type);
        const placeholderWeapons = results.filter(r => r.usedPlaceholder).map(r => r.type);
        
        console.log(`======= WEAPON PRELOADING COMPLETE =======`);
        console.log(`✅ Loaded weapons: [${loadedWeapons.join(', ')}]`);
        if (placeholderWeapons.length) {
          console.log(`🔶 Using placeholders for: [${placeholderWeapons.join(', ')}]`);
        }
        
        // Dispatch an event that can be listened for
        window.dispatchEvent(new CustomEvent('weapons-loaded', { 
          detail: { 
            loaded: loadedWeapons,
            placeholders: placeholderWeapons
          }
        }));
      });
  }
  
  // Create placeholder models for weapons that can't be loaded
  createPlaceholderWeaponModels() {
    this.placeholderModels = {};
    
    // Create a simple rifle placeholder
    const riflePlaceholder = this.createRiflePlaceholder();
    this.placeholderModels.rifle = {
      scene: riflePlaceholder,
      animations: [] // No animations for the placeholder
    };
  }
  
  // Create a simple placeholder rifle model using Three.js geometry
  createRiflePlaceholder() {
    const group = new THREE.Group();
    
    // Create a simple rifle shape
    const barrelGeometry = new THREE.BoxGeometry(0.05, 0.05, 0.4);
    const barrelMaterial = new THREE.MeshStandardMaterial({ color: 0x222222 });
    const barrel = new THREE.Mesh(barrelGeometry, barrelMaterial);
    barrel.position.z = -0.2;
    
    const handleGeometry = new THREE.BoxGeometry(0.04, 0.1, 0.15);
    const handleMaterial = new THREE.MeshStandardMaterial({ color: 0x8B4513 });
    const handle = new THREE.Mesh(handleGeometry, handleMaterial);
    handle.position.y = -0.05;
    handle.position.z = 0;
    
    // Add the parts to the group
    group.add(barrel);
    group.add(handle);
    
    // Set position and rotation for attachment to player model
    group.position.set(0, 0, 0);
    group.rotation.set(0, 0, 0);
    
    // Add user data for identification
    group.userData = { isPlaceholder: true, modelType: 'rifle' };
    
    return group;
  }
  
  // Setup character models
  setupCharacterModels() {
    // Define GLB model configurations for different character types
    this.characterModels = {
      soldier: {
        modelPath: '/models/players/soldier.glb',
        handsModelPath: '/models/players/hands.glb', // Add hands model path
        height: 1.2,
        scale: 1.0,
        speed: 6,  // Standard speed for Soldier
        animations: {
          idle: 'rifle_idle',
          walk: 'rifle_run_fwd', // Use rifle forward as walk
          run: 'rifle_run_fwd',  // Use same animation for run but will be played faster
          walkBackward: 'rifle_run_bwd',
          walkLeft: 'rifle_run_left',
          walkRight: 'rifle_run_right',
          death: 'rifle_idle_death',  // Default death animation
          // Death animations
          idleDeath: 'rifle_idle_death',
          runDeath: 'rifle_run_death',
          crouchDeath: 'rifle_crouch_death',
          // Add new crouch animations
          crouchIdle: 'rifle_crouch_idle',
          crouchForward: 'rifle_crouch_fwd',
          crouchBackward: 'rifle_crouch_bwd',
          crouchLeft: 'rifle_crouch_left',
          crouchRight: 'rifle_crouch_right',
          pistolIdle: 'pistol_idle',
          // Add jumping animations
          jump: 'rifle_idle',  // Use idle animation for jump (as placeholder)
          fall: 'rifle_idle'   // Use idle animation for fall (as placeholder)
        }
      },
      // Add an alias for 'Soldier' with capital S to handle case inconsistency in the codebase
      Soldier: {
        modelPath: '/models/players/soldier.glb',
        handsModelPath: '/models/players/hands.glb',
        height: 1.2,
        scale: 1.0,
        speed: 6,
        animations: {
          idle: 'rifle_idle',
          walk: 'rifle_run_fwd',
          run: 'rifle_run_fwd',
          walkBackward: 'rifle_run_bwd',
          walkLeft: 'rifle_run_left',
          walkRight: 'rifle_run_right',
          death: 'rifle_idle_death',
          idleDeath: 'rifle_idle_death',
          runDeath: 'rifle_run_death',
          crouchDeath: 'rifle_crouch_death',
          crouchIdle: 'rifle_crouch_idle',
          crouchForward: 'rifle_crouch_fwd',
          crouchBackward: 'rifle_crouch_bwd',
          crouchLeft: 'rifle_crouch_left',
          crouchRight: 'rifle_crouch_right',
          pistolIdle: 'pistol_idle'
        }
      },
      scout: {
        modelPath: '/models/players/scout.glb',
        handsModelPath: '/models/players/hands.glb', // Add hands model path
        height: 1.3,
        scale: 0.9,
        speed: 9,  // Faster speed for Scout
        animations: {
          idle: 'rifle_idle',
          walk: 'rifle_run_fwd',
          run: 'rifle_run_fwd',
          walkBackward: 'rifle_run_bwd',
          walkLeft: 'rifle_run_left',
          walkRight: 'rifle_run_right',
          death: 'rifle_idle_death',
          idleDeath: 'rifle_idle_death',
          runDeath: 'rifle_run_death',
          crouchDeath: 'rifle_crouch_death',
          // Add crouching animations for scout
          crouchIdle: 'rifle_crouch_idle',
          crouchForward: 'rifle_crouch_fwd',
          crouchBackward: 'rifle_crouch_bwd',
          crouchLeft: 'rifle_crouch_left',
          crouchRight: 'rifle_crouch_right',
          // Add jumping animations
          jump: 'rifle_idle',  // Use idle animation for jump (as placeholder)
          fall: 'rifle_idle'   // Use idle animation for fall (as placeholder)
        }
      }
    };
    
    // Preload models
    Object.keys(this.characterModels).forEach(characterType => {
      // Skip duplicate model paths (e.g., 'Soldier' is same as 'soldier')
      if (characterType === 'Soldier') return;
      
      this.loadModel(this.characterModels[characterType].modelPath);
      // Also preload hands model
      if (this.characterModels[characterType].handsModelPath) {
        this.loadModel(this.characterModels[characterType].handsModelPath);
      }
    });
  }
  
  // Load a GLTF/GLB model and store in cache
  loadModel(modelPath) {
    // Return from cache if already loaded
    //console.log("Loading model:", modelPath);
    if (this.modelCache[modelPath]) {
      return Promise.resolve(this.modelCache[modelPath]);
    }
    
    return new Promise((resolve, reject) => {
      this.gltfLoader.load(
        modelPath,
        (gltf) => {
          // Mark the model as non-reactive (for Vue)
          const model = markRaw(gltf);
          
          // Setup model for shadowing
          model.scene.traverse((child) => {
            if (child.isMesh) {
              child.castShadow = true;
              child.receiveShadow = true;
              
              // Make hitbox meshes invisible
              if (child.name.startsWith('Hitbox')) {
                child.visible = false;
              }
              
              // Apply polygon offset to fix shadows when objects intersect with terrain
              if (child.material) {
                // Ensure we have a unique material instance
                if (!child.material._isCloned) {
                  if (Array.isArray(child.material)) {
                    child.material = child.material.map(m => m.clone());
                    child.material.forEach(m => m._isCloned = true);
                  } else {
                    child.material = child.material.clone();
                    child.material._isCloned = true;
                  }
                }
                
                // Apply polygon offset to the material(s)
                if (Array.isArray(child.material)) {
                  child.material.forEach(material => {
                    material.polygonOffset = true;
                    material.polygonOffsetFactor = -0.2;
                    material.polygonOffsetUnits = -1;
                  });
                } else {
                  child.material.polygonOffset = true;
                  child.material.polygonOffsetFactor = -0.2;
                  child.material.polygonOffsetUnits = -1;
                }
              }
            }
          });
          
          // Store in cache
          this.modelCache[modelPath] = model;
          console.log(`Model loaded and cached: ${modelPath}`);
          resolve(model);
        },
        (xhr) => {
          console.log(`${modelPath} loading: ${(xhr.loaded / xhr.total * 100).toFixed(2)}% loaded`);
        },
        (error) => {
          console.error(`Error loading model ${modelPath}:`, error);
          reject(error);
        }
      );
    });
  }
  
  // Helper method to properly deep clone a GLTF model with all transformations
  deepCloneGltf(gltf) {
    // Create a new clone object with the same structure
    const clone = {
      animations: gltf.animations,
      scene: new THREE.Group()
    };
    
    // Map to track original objects to their clones for references
    const cloneMap = new Map();
    
    // Clone all the child objects properly
    gltf.scene.traverse((originalObj) => {
      let clonedObj;
      
      // Clone different object types appropriately
      if (originalObj.isSkinnedMesh) {
        // For skinned meshes, we need to clone geometry and material
        const geometry = originalObj.geometry.clone();
        const material = originalObj.material.clone();
        clonedObj = new THREE.SkinnedMesh(geometry, material);
        
        // Copy bind matrix and mode
        clonedObj.bindMatrix.copy(originalObj.bindMatrix);
        clonedObj.bindMode = originalObj.bindMode;
        
        // We'll handle the skeleton after all objects are cloned
        cloneMap.set(originalObj, clonedObj);
      } 
      else if (originalObj.isMesh) {
        // Regular meshes
        const geometry = originalObj.geometry.clone();
        const material = originalObj.material.clone();
        clonedObj = new THREE.Mesh(geometry, material);
        cloneMap.set(originalObj, clonedObj);
      }
      else if (originalObj.isGroup) {
        // Groups
        clonedObj = new THREE.Group();
        cloneMap.set(originalObj, clonedObj);
      }
      else if (originalObj.isBone) {
        // Bones
        clonedObj = new THREE.Bone();
        cloneMap.set(originalObj, clonedObj);
      }
      else {
        // Other object types (like empty nodes)
        clonedObj = new THREE.Object3D();
        cloneMap.set(originalObj, clonedObj);
      }
      
      // Copy the transformation properties
      if (clonedObj && originalObj) {
        clonedObj.position.copy(originalObj.position);
        clonedObj.quaternion.copy(originalObj.quaternion);
        clonedObj.scale.copy(originalObj.scale);
        clonedObj.matrix.copy(originalObj.matrix);
        clonedObj.matrixWorld.copy(originalObj.matrixWorld);
        clonedObj.matrixAutoUpdate = originalObj.matrixAutoUpdate;
        clonedObj.matrixWorldNeedsUpdate = originalObj.matrixWorldNeedsUpdate;
        
        // Copy the name and userData
        clonedObj.name = originalObj.name;
        clonedObj.userData = JSON.parse(JSON.stringify(originalObj.userData));
        
        // Copy visibility and other basic properties
        clonedObj.visible = originalObj.visible;
        clonedObj.castShadow = originalObj.castShadow;
        clonedObj.receiveShadow = originalObj.receiveShadow;
      }
    });
    
    // Recreate the hierarchy
    gltf.scene.traverse((originalObj) => {
      const clonedObj = cloneMap.get(originalObj);
      
      if (clonedObj && originalObj !== gltf.scene) {
        // Find the parent's clone and add this object as a child
        const originalParent = originalObj.parent;
        if (originalParent) {
          const clonedParent = cloneMap.get(originalParent);
          if (clonedParent) {
            clonedParent.add(clonedObj);
          }
        }
      }
    });
    
    // Set up skeletons and bones
    gltf.scene.traverse((originalObj) => {
      if (originalObj.isSkinnedMesh) {
        const clonedMesh = cloneMap.get(originalObj);
        const originalSkeleton = originalObj.skeleton;
        
        // Create a new skeleton
        const bones = [];
        const boneInverses = [];
        
        // Map the bones
        for (let i = 0; i < originalSkeleton.bones.length; i++) {
          const originalBone = originalSkeleton.bones[i];
          const clonedBone = cloneMap.get(originalBone);
          
          if (clonedBone) {
            bones.push(clonedBone);
            boneInverses.push(originalSkeleton.boneInverses[i].clone());
          }
        }
        
        // Create and assign the new skeleton
        clonedMesh.skeleton = new THREE.Skeleton(bones, boneInverses);
      }
    });
    
    // Build the root of the clone
    gltf.scene.children.forEach((child) => {
      const clonedChild = cloneMap.get(child);
      if (clonedChild) {
        clone.scene.add(clonedChild);
      }
    });
    
    // Make sure the scene is marked as non-reactive (for Vue)
    clone.scene = markRaw(clone.scene);
    
    return clone;
  }
  
  // Create a player (local or remote)
  createPlayer(options) {
    const {
      id,
      position,
      color,
      team,
      character = 'soldier',
      isLocalPlayer = false,
      forcedSpawn = false
    } = options;
    
    try {
      // First check if this player already exists and remove it if it does
      if ((isLocalPlayer && this.localPlayer) || this.players[id]) {
        console.log(`Player ${id} already exists, removing old model before creating new one`);
        this.removePlayer(id);
      }
      
      // Normalize character type to lowercase for consistent handling
      const characterType = character.toLowerCase();
      
      // Get the character model configuration
      const characterConfig = this.characterModels[character] || this.characterModels.soldier;
      
      // Try to get a model from the pool first - prioritize pool usage for ALL players
      let playerGroup;
      
      if (this.shouldUsePoolForPlayer(character, isLocalPlayer)) {
        console.log(`Getting ${characterType} model from pool for player ${id} (isLocal: ${isLocalPlayer})...`);
        playerGroup = this.playerPools[characterType].getModel(id);
        
        if (playerGroup) {
          console.log(`Successfully retrieved pooled model for player ${id}`);
          
          // Set position and rotation
          if (position) {
            playerGroup.position.set(position.x, position.y, position.z);
          } else {
            console.warn(`No position provided for player ${id}, using default position`);
            playerGroup.position.set(0, 0.05, 0);
          }
          
          // Update userData with character type and player ID
          playerGroup.userData.character = characterType;
          playerGroup.userData.playerId = id;
          playerGroup.userData.isPlayer = true; // Important for hit detection
          playerGroup.userData.isLocalPlayer = isLocalPlayer;
          
          // === START ADDED CODE ===
          // ALSO update userData for all child meshes within the pooled model
          playerGroup.traverse(child => {
            if (child.isMesh || child.isBone) { // Update meshes and bones
              if (!child.userData) child.userData = {};
              child.userData.playerId = id; 
              child.userData.isPlayer = true; // Ensure this is set too
              
              // Reset visibility for hitboxes if needed (pool might leave them visible)
              if (child.name.startsWith('Hitbox')) {
                child.visible = false;
              }
            }
          });
          // === END ADDED CODE ===
          
          // For local player, handle visibility differently
          if (isLocalPlayer) {
            // For first-person perspective, hide the player model
            playerGroup.visible = false;
          } else {
            playerGroup.visible = true;
          }
          
          // Initialize animations for the pooled model if not already done
          if (!playerGroup.userData.currentAnimation || playerGroup.userData.currentAnimation !== 'idle') {
            this.initializePlayerAnimations(playerGroup, characterConfig, id);
          }
          
          // Ensure bones are updated before weapon attachment
          playerGroup.updateMatrixWorld(true);
          if (playerGroup.children[0]) {
            playerGroup.children[0].updateMatrixWorld(true);
          }
          
          // Store player group in players object
          if (isLocalPlayer) {
            this.localPlayer = playerGroup;
          } else {
            this.players[id] = playerGroup;
          }
          
          // Wait a frame to ensure all matrices are updated before attaching weapon
          if (!isLocalPlayer) {
            // Only attach weapon to non-local players (local player uses first-person weapons)
            setTimeout(() => {
              this.attachWeaponToPlayer(playerGroup, 'rifle');
            }, 0);
          }
          
          console.log(`Successfully set up pooled model for player ${id}`);
          return playerGroup;
        } else {
          console.warn(`Could not get model from pool for player ${id}, creating new model as fallback`);
          // Continue with normal model creation if pool model not available
        }
      } else {
        console.warn(`No pool available for ${characterType}, creating new model as fallback`);
      }
      
      // If we reach here, no pool model was available - create a new one as fallback
      // Create a new player group
      playerGroup = markRaw(new THREE.Group());
      playerGroup.userData = {
        character: characterType,
        playerId: id,
        animations: {},
        isPooled: false,
        isPlayer: true, // Important for hit detection
        isLocalPlayer: isLocalPlayer
      };
      
      // Set initial position
      if (position) {
        playerGroup.position.set(position.x, position.y, position.z);
      } else {
        console.warn(`No position provided for player ${id}, using default position`);
        playerGroup.position.set(0, 0.05, 0);
      }
      
      // Create a temporary mesh while the model loads
      const tempGeo = new THREE.BoxGeometry(0.5, 1.5, 0.5);
      // Use color from options or a default gray color
      const defaultColor = 0x888888; 
      const tempMat = new THREE.MeshBasicMaterial({ color: color || defaultColor });
      const tempMesh = markRaw(new THREE.Mesh(tempGeo, tempMat));
      tempMesh.position.y = 0.75; // Half height
      playerGroup.add(tempMesh);
      
      // Add to scene immediately
      this.scene.add(playerGroup);
      
      // Store player group in players object before model loads
      if (!isLocalPlayer) {
        this.players[id] = playerGroup;
      } else {
        this.localPlayer = playerGroup;
        // Hide the local player's model in first-person view
        playerGroup.visible = false;
      }
      
      // Load the actual model
      this.loadModel(characterConfig.modelPath)
        .then(gltf => {
          // Remove the temporary mesh
          playerGroup.remove(tempMesh);
          
          // Clone the model to avoid sharing issues with multiple players
          const modelClone = this.deepCloneGltf(gltf);
          
          // Apply player color to the model materials if color provided
          const playerColorObj = new THREE.Color(color || defaultColor);
          modelClone.scene.traverse(child => {
            if (child.isMesh) {
              // Mark meshes for hit detection
              child.userData.isPlayer = true;
              child.userData.playerId = id;
              
              // If this is the local player, make meshes invisible but still cast shadows
              if (isLocalPlayer) {
                child.visible = false;
                child.castShadow = true;
              } else {
                // For other players, make meshes fully visible and cast shadows
                child.visible = true;
                child.castShadow = true;
                
                // Make hitbox meshes invisible
                if (child.name.startsWith('Hitbox')) {
                  child.visible = false;
                }
              }
              
              // Apply player color to certain parts of the model
              if (child.name.includes('Uniform') || child.name.includes('Team')) {
                // Make sure we have a clone of the material to avoid affecting other instances
                if (!child.material._isCloned) {
                  child.material = child.material.clone();
                  child.material._isCloned = true;
                }
                child.material.color.set(playerColorObj);
              }
            }
          });
          
          // Add the model to the group
          playerGroup.add(modelClone.scene);
          
          // Store model-related properties in userData
          playerGroup.userData.characterType = characterType;
          
          // Set correct rotation for the model
          if (characterType === 'soldier' || characterType === 'scout') {
            modelClone.scene.rotation.y = Math.PI; // 180 degrees in radians
          }
          
          // Setup animations if available
          if (gltf.animations && gltf.animations.length > 0) {
            const mixer = markRaw(new THREE.AnimationMixer(modelClone.scene));
            playerGroup.userData.mixer = mixer;
            
            // Map animation clips
            gltf.animations.forEach(animation => {
              // Store the animation with its name
              playerGroup.userData.animations[animation.name] = mixer.clipAction(animation);
            });
            
            // Initialize animations using common method for consistency
            this.initializePlayerAnimations(playerGroup, characterConfig, id);
          } else {
            console.warn(`No animations found in model for ${characterType}`);
          }
          
          // Store bone references for weapon attachment
          playerGroup.userData.bones = {};
          modelClone.scene.traverse(obj => {
            if (obj.isBone) {
              playerGroup.userData.bones[obj.name] = obj;
            }
          });
          
          // Attach weapon for non-local players only
          if (!isLocalPlayer) {
            // Ensure matrices are updated
            playerGroup.updateMatrixWorld(true);
            
            // Attach rifle to player
            setTimeout(() => {
              this.attachWeaponToPlayer(playerGroup, 'rifle');
            }, 0);
          }
          
          return playerGroup;
        })
        .catch(error => {
          console.error(`Error loading player model for ${id}:`, error);
          return playerGroup; // Return group with temporary mesh
        });
      
      return playerGroup;
    } catch (error) {
      console.error('Error in createPlayer:', error);
      return null;
    }
  }
  
  // Helper method to initialize animations consistently for both pooled and new models
  initializePlayerAnimations(playerGroup, characterConfig, playerId) {
    console.log(`Initializing animations for player ${playerId}`);
    
    try {
        if (!playerGroup || !playerGroup.userData) {
            console.warn(`Cannot initialize animations: Invalid player group for ${playerId}`);
            return;
        }
        
        const isLocalPlayer = playerGroup.userData.isLocalPlayer === true;
        const isPooledModel = playerGroup.userData.isPooled === true;
        
        // Check if animations are available
        if (!playerGroup.userData.animations || Object.keys(playerGroup.userData.animations).length === 0) {
            console.warn(`No animations available for player ${playerId}`);
            return;
        }
        
        // Use the animation controller to set up animations
        this.animationController.initializePlayer(playerGroup, characterConfig, playerId);
        
        // For local player, we need to handle hands specifically
        if (isLocalPlayer) {
            // Store character config for animation sync with hands
            playerGroup.userData.animationMappings = characterConfig.animations;
            
            // Create the direct animation mappings needed for synchronizing with hands
            Object.entries(characterConfig.animations).forEach(([key, animName]) => {
                if (playerGroup.userData.animations[animName]) {
                    playerGroup.userData[key] = animName;
                }
            });
        }
    } catch (error) {
        console.error(`Error initializing animations for player ${playerId}:`, error);
    }
}
  
  // Update player position with proper animation handling
  updatePlayerPosition(playerGroup, position) {
    if (!playerGroup) return;

    // Skip position updates if player is dead
    if (playerGroup.userData?.isDead) {
        return;
    }

    // Store the target position for interpolation instead of immediately setting it
    if (!this.positionBuffer[playerGroup.uuid]) {
        this.positionBuffer[playerGroup.uuid] = {
            targetPosition: position.clone(),
            lastUpdateTime: Date.now()
        };
    } else {
        this.positionBuffer[playerGroup.uuid].targetPosition.copy(position);
        this.positionBuffer[playerGroup.uuid].lastUpdateTime = Date.now();
    }
    
    // Store in userData for animation calculation
    if (!playerGroup.userData) playerGroup.userData = {};
    if (!playerGroup.userData.lastPosition) {
        playerGroup.userData.lastPosition = playerGroup.position.clone();
    }
    
    // Adjust Y position for terrain height if available
    if (this.mapScene && typeof this.mapScene.getTerrainHeightAt === 'function') {
        const terrainHeight = this.mapScene.getTerrainHeightAt(position.x, position.z);
        if (terrainHeight !== undefined) {
            this.positionBuffer[playerGroup.uuid].targetPosition.y = 
                Math.max(position.y, terrainHeight + TERRAIN_Y_OFFSET); // Ensure player is above terrain
        }
    }
    
    // Only proceed if we have both current and last position
    if (playerGroup.userData && playerGroup.userData.lastPosition) {
        // Skip animation update if player is dead
        if (playerGroup.userData.isDead || playerGroup.userData.currentAnimation === 'death') {
            // Don't change animation if player is dead
            return;
        }
        
        const targetPos = this.positionBuffer[playerGroup.uuid].targetPosition;
        
        // Calculate movement vector towards target position
        const moveVector = new THREE.Vector3().subVectors(targetPos, playerGroup.position);
        
        // Calculate distance and direction
        const distance = moveVector.length();
        const isMoving = distance > 0.01; // Small threshold to detect movement
        const isRunning = distance > 0.1; // Higher threshold for running
        
        // Check if player is in crouching state
        const isCrouching = playerGroup.userData.isCrouching || false;
        const isJumping = playerGroup.userData.isJumping || false;
        const jumpVelocity = playerGroup.userData.jumpVelocity || 0;
        const isDead = playerGroup.userData.isDead || false;
        
        // Get player ID for debugging
        const playerId = playerGroup.userData.playerId || playerGroup.uuid;
        
        // Determine movement direction relative to player's facing direction
        let moveDirection = 'forward';
        
        if (isMoving) {
            // Get the player's forward direction
            const forwardVector = new THREE.Vector3(0, 0, -1);
            forwardVector.applyAxisAngle(new THREE.Vector3(0, 1, 0), playerGroup.rotation.y);
            
            // Get the right vector (perpendicular to forward)
            const rightVector = new THREE.Vector3(1, 0, 0);
            rightVector.applyAxisAngle(new THREE.Vector3(0, 1, 0), playerGroup.rotation.y);
            
            // Normalize the movement vector
            const normalizedMove = moveVector.clone().normalize();
            
            // Calculate dot products to determine direction
            const forwardDot = forwardVector.dot(normalizedMove); // +1 if moving forward, -1 if backward
            const rightDot = rightVector.dot(normalizedMove);     // +1 if moving right, -1 if left
            
            // Determine primary movement direction
            if (Math.abs(forwardDot) > Math.abs(rightDot)) {
                // Moving primarily forward/backward
                moveDirection = forwardDot > 0 ? 'forward' : 'backward';
            } else {
                // Moving primarily left/right
                moveDirection = rightDot > 0 ? 'right' : 'left';
            }
        }
        
        // Use AnimationController to determine and play appropriate animation
        const conditions = {
            isDead,
            isJumping,
            jumpVelocity,
            isCrouching,
            isMoving,
            isRunning,
            moveDirection
        };
        
        this.animationController.updateAnimation(playerGroup, conditions);
    }
}
  
  // Play an animation for a player
  playPlayerAnimation(playerGroup, animationKey, transitionDuration = 0.2) {
    if (!playerGroup || !playerGroup.userData) {
        console.warn(`Cannot play animation: Invalid player group`);
        return;
    }
    
    const playerId = playerGroup.userData.playerId || 'unknown';
    
    // Delegate to the animation controller
    return this.animationController.playAnimation(playerGroup, animationKey);
}
  
  // Add helper method to determine blend duration
  getBlendDuration(fromAnim, toAnim) {
    if (toAnim === 'idle' || toAnim === 'crouchIdle') {
      return this.idleBlendDuration;
    } else if (toAnim.includes('run') || fromAnim?.includes('run')) {
      return this.runningBlendDuration;
    } else if (['walkLeft', 'walkRight', 'walkBackward'].includes(toAnim)) {
      return this.directionalBlendDuration;
    }
    return this.defaultBlendDuration;
  }
  
  // Modify removePlayer to add more detailed pooled model logging
  removePlayer(playerId) {
    // Check if we're removing the local player
    if (this.localPlayer && (!playerId || playerId === this.localPlayer.userData?.playerId)) {
      console.log(`Removing local player model ${playerId} from scene`);
      
      // Clean up any animations and mixer
      if (this.localPlayer.userData && this.localPlayer.userData.mixer) {
        this.localPlayer.userData.mixer.stopAllAction();
        // No need to dispose mixer as it will be garbage collected
      }
      
      // Remove any hitbox visualization if it exists
      if (this.hitboxHelpers['local-player']) {
        this.scene.remove(this.hitboxHelpers['local-player']);
        delete this.hitboxHelpers['local-player'];
      }
      
      // Remove from scene and properly dispose
      this.disposeObject(this.localPlayer);
      this.scene.remove(this.localPlayer);
      this.localPlayer = null;
    }
    // Check if we're removing another player
    else if (this.players[playerId]) {
      console.log(`Removing player model ${playerId} from scene`);
      
      // Check if this is a pooled model
      const playerGroup = this.players[playerId];
      const isPooledModel = playerGroup.userData?.isPooled === true;
      const characterType = playerGroup.userData?.character || 'soldier';
      
      // Use safe logging to avoid circular references
      console.log(`Player ${playerId} model details:`, {
        isPooled: isPooledModel,
        characterType,
        hasAnimations: !!playerGroup.userData?.animations,
        animationCount: playerGroup.userData?.animations ? Object.keys(playerGroup.userData.animations).length : 0,
        poolIndex: playerGroup.userData?.poolIndex
      });
      
      // Clean up any animations and mixer
      if (playerGroup.userData && playerGroup.userData.mixer) {
        console.log(`Stopping all animations for player ${playerId}`);
        playerGroup.userData.mixer.stopAllAction();
        // No need to dispose mixer as it will be garbage collected
      }
      
      // Remove any hitbox visualization if it exists
      if (this.hitboxHelpers[playerId]) {
        this.scene.remove(this.hitboxHelpers[playerId]);
        delete this.hitboxHelpers[playerId];
      }
      
      // Clean up interpolation data
      if (this.positionBuffer[playerGroup.uuid]) {
        delete this.positionBuffer[playerGroup.uuid];
      }
      
      // Clean up animation transitions
      if (this.animationTransitions[playerId]) {
        delete this.animationTransitions[playerId];
      }
      
      // Return to pool if it's a pooled model
      if (isPooledModel && characterType === 'soldier' && this.playerPools.soldier) {
      //  console.log(`Returning pooled model for player ${playerId} to the pool`);
        
        try {
          this.playerPools.soldier.returnModel(playerId);
          console.log(`Successfully returned model to pool for player ${playerId}`);
        } catch (error) {
          console.error(`Error returning model to pool for player ${playerId}:`, error);
          // Fall back to normal disposal if returning to pool fails
          this.disposeObject(playerGroup);
          this.scene.remove(playerGroup);
        }
      } else {
        console.log(`Model for player ${playerId} is not pooled, disposing normally`);
        // Dispose of materials and geometries for non-pooled models
        this.disposeObject(playerGroup);
        
        // Remove from scene
        this.scene.remove(playerGroup);
      }
      
      // Remove from players object
      delete this.players[playerId];
      console.log(`Completed removal of player ${playerId}`);
    } else {
      console.warn(`Player ${playerId} not found, cannot remove`);
    }
    
    // Clean up movement state
    if (this.movementStates[playerId]) {
      if (this.movementStates[playerId].pendingIdleTimeout) {
        clearTimeout(this.movementStates[playerId].pendingIdleTimeout);
      }
      delete this.movementStates[playerId];
    }
    
    // Clean up animation state
    if (this.animationStates[playerId]) {
      delete this.animationStates[playerId];
    }
  }
  
  // Helper method to recursively dispose of THREE.js objects
  disposeObject(object) {
    if (!object) return;
    
    // Recursively process all children
    if (object.children && object.children.length > 0) {
      // Use a for loop instead of forEach to handle the case where children array might change during iteration
      for (let i = object.children.length - 1; i >= 0; i--) {
        this.disposeObject(object.children[i]);
      }
    }
    
    // Dispose of geometries and materials
    if (object.geometry) {
      object.geometry.dispose();
    }
    
    if (object.material) {
      // Handle array of materials
      if (Array.isArray(object.material)) {
        object.material.forEach(material => material.dispose());
      } else {
        // Handle single material
        if (object.material.map) object.material.map.dispose();
        if (object.material.lightMap) object.material.lightMap.dispose();
        if (object.material.bumpMap) object.material.bumpMap.dispose();
        if (object.material.normalMap) object.material.normalMap.dispose();
        if (object.material.specularMap) object.material.specularMap.dispose();
        if (object.material.envMap) object.material.envMap.dispose();
        
        object.material.dispose();
      }
    }
    
    // Clear any references that might prevent garbage collection
    if (object.userData) {
      // Clear all properties except those that might be needed for identification
      const preservedData = {
        playerId: object.userData.playerId,
        team: object.userData.team,
        character: object.userData.character
      };
      
      // Reset userData but keep essential properties
      object.userData = preservedData;
    }
  }
  
  // Add hitbox visualization for a specific player
  addHitboxVisualization(playerObj, id) {
    if (!playerObj || !playerObj.userData) return;
    
    // Get character config
    const characterType = playerObj.userData.character || 'soldier';
    const characterConfig = this.characterModels[characterType] || this.characterModels.soldier;
    const characterScale = characterConfig.scale;
    
    // Create hitbox dimensions based on character model (same as in checkCollisions)
    const playerWidth = 0.5 * characterScale;
    const playerDepth = 0.4 * characterScale;
    let playerHeight = characterConfig.height;
    
    // Check if the player is crouching
    const isCrouching = playerObj.userData.currentAnimation && 
      (playerObj.userData.currentAnimation.startsWith('crouch'));
    
    // Adjust height for crouching
    if (isCrouching) {
      playerHeight *= 0.6; // Same 60% reduction as in checkCollisions
    }
    
    // Create a box geometry for the hitbox
    const hitboxGeometry = new THREE.BoxGeometry(playerWidth, playerHeight, playerDepth);
    const hitboxMaterial = new THREE.MeshBasicMaterial({
      color: 0x00ff00, // Green for visibility
      wireframe: true,
      transparent: true,
      opacity: 0.5
    });
    
    const hitboxMesh = new THREE.Mesh(hitboxGeometry, hitboxMaterial);
    
    // Position the hitbox to match the player
    // Note: The player's origin is at the feet, but the box geometry is centered
    hitboxMesh.position.y = playerHeight / 2;
    
    // Add the hitbox to the player
    playerObj.add(hitboxMesh);
    
    // Store reference to the hitbox mesh for later removal
    this.hitboxHelpers[id] = hitboxMesh;
  }
  
  // Method to update hitbox visualizations
  updateHitboxVisualizations() {
    // First, clear any existing visualizations
    this.clearHitboxVisualizations();
    
    // Add hitbox visualization for local player
    if (this.localPlayer) {
      this.addHitboxVisualization(this.localPlayer, 'local');
    }
    
    // Add hitbox visualizations for other players
    Object.entries(this.players).forEach(([playerId, player]) => {
      this.addHitboxVisualization(player, playerId);
    });
  }
  
  // Method to clear all hitbox visualizations
  clearHitboxVisualizations() {
    // Remove all hitbox visualizations
    Object.entries(this.hitboxHelpers).forEach(([id, hitboxMesh]) => {
      if (hitboxMesh && hitboxMesh.parent) {
        hitboxMesh.parent.remove(hitboxMesh);
      }
    });
    
    // Clear the helpers object
    this.hitboxHelpers = {};
  }
  
  // Update method to be called every frame
  update(deltaTime = 0.016) {
    // Ensure we have a valid delta time value
    const delta = deltaTime || 0.016; // Use provided delta or default to 60fps (0.016s)
    
    try {
      // Update all mixers with safer null handling
      Object.values(this.players).forEach(player => {
        if (!player) return; // Skip null players
        if (player.userData && player.userData.mixer) {
          player.userData.mixer.update(delta);
        }
        // Hands mixer updates now handled by WeaponSystem
      });
      
      // Update local player mixer if it exists
      if (this.localPlayer && this.localPlayer.userData) {
        if (this.localPlayer.userData.mixer) {
          this.localPlayer.userData.mixer.update(delta);
        }
        // Hands mixer updates now handled by WeaponSystem
      }
      
      // Rest of the update method is unchanged
      // Update animation transitions
      if (this.useAnimationBlending) {
        Object.values(this.players).forEach(player => {
          if (!player) return; // Skip null players
          this.updateAnimationBlending(player, delta);
        });
        
        if (this.localPlayer) {
          this.updateAnimationBlending(this.localPlayer, delta);
        }
      }
      
      // Update interpolation for remote players with safer null handling
      Object.values(this.players).forEach(player => {
        if (!player) return; // Skip null players
        
        // Skip players that don't have position buffer data
        if (!this.positionBuffer[player.uuid]) {
          return;
        }
        
        // Get the target position from buffer
        const targetPosition = this.positionBuffer[player.uuid].targetPosition;
        if (!targetPosition) return; // Skip if no target position
        
        // Only interpolate if we need to move
        if (!player.position.equals(targetPosition)) {
          // Calculate time-adjusted interpolation factor
          // This ensures consistent interpolation speed regardless of frame rate
          const adjustedFactor = Math.min(1.0, this.interpolationFactor * (delta / 0.016));
          
          // Lerp towards the target position
          player.position.lerp(targetPosition, adjustedFactor);
          
          // Ensure player has userData
          if (!player.userData) {
            player.userData = {};
            console.warn('Player has no userData, creating empty object');
          }
          
          // Update last position for animation calculation
          if (!player.userData.lastPosition) {
            player.userData.lastPosition = new THREE.Vector3();
          }
          
          // Periodically update the last position to detect new movement
          const now = Date.now();
          if (!player.userData.lastPositionUpdateTime || 
              now - player.userData.lastPositionUpdateTime > 100) {
            player.userData.lastPosition.copy(player.position);
            player.userData.lastPositionUpdateTime = now;
          }
        }
      });
      
      // Update weapon attachment positions if needed
      if (this.weaponUpdateFunctions) {
        this.weaponUpdateFunctions.forEach(updateFn => {
          if (typeof updateFn === 'function') {
            try {
              updateFn();
            } catch (e) {
              console.error('Error updating weapon position:', e);
            }
          }
        });
      }
      
      // Check player animations periodically to fix "stuck" animations
      const now = Date.now();
      if (!this.lastAnimationCheck || now - this.lastAnimationCheck > 500) {
        this.lastAnimationCheck = now;
        
        Object.values(this.players).forEach(player => {
          if (!player) return; // Skip null players
          this.checkAndFixPlayerAnimation(player);
        });
        
        if (this.localPlayer) {
          this.checkAndFixPlayerAnimation(this.localPlayer);
        }
      }
      
    } catch (error) {
      console.error('Error in PlayerManager update:', error);
    }
  }
  
  // Add a new method for position interpolation
  interpolatePlayerPosition(playerGroup, deltaTime) {
    if (!playerGroup || !this.positionBuffer[playerGroup.uuid]) return;
    
    const bufferData = this.positionBuffer[playerGroup.uuid];
    const targetPosition = bufferData.targetPosition;
    
    // Skip interpolation if player is local
    if (playerGroup === this.localPlayer) return;
    
    // Handle dead players differently - don't interpolate position for dead players
    if (this.isPlayerDead(playerGroup)) {
      // Don't move dead bodies - they should stay where they died
      return;
    }
    
    // Calculate a dynamic interpolation factor based on distance
    const distance = playerGroup.position.distanceTo(targetPosition);
    
    // If the player is very far from target (teleporting/spawning), snap to position
    if (distance > 5) {
      playerGroup.position.copy(targetPosition);
      return;
    }
    
    // Calculate time since last update
    const timeSinceUpdate = (Date.now() - bufferData.lastUpdateTime) / 1000;
    
    // Use a higher interpolation factor for lateral movement
    const lateralDistance = Math.sqrt(
      Math.pow(targetPosition.x - playerGroup.position.x, 2) + 
      Math.pow(targetPosition.z - playerGroup.position.z, 2)
    );
    
    // Adjust interpolation factor based on lateral movement
    let factor = this.interpolationFactor;
    if (lateralDistance > 0.5) {
      // More aggressive interpolation for significant lateral movement
      factor = Math.min(0.4, factor * 2);
    }
    
    // Before interpolation, store the previous position
    const previousPosition = playerGroup.position.clone();
    
    // Smooth interpolation toward target position
    playerGroup.position.lerp(targetPosition, factor);
    
    // Calculate if significant movement occurred during this interpolation
    const movedDistance = playerGroup.position.distanceTo(previousPosition);
    
    // If player hasn't moved significantly in the last second, make sure they're in idle
    if (movedDistance < 0.005 && timeSinceUpdate > 1.0 && 
        playerGroup.userData && playerGroup.userData.currentAnimation && 
        playerGroup.userData.currentAnimation !== 'idle' && 
        playerGroup.userData.currentAnimation !== 'crouchIdle' &&
        !playerGroup.userData.currentAnimation.includes('death')) {
      
      // Skip animation changes for dead players
      if (!this.isPlayerDead(playerGroup)) {
        // Switch to idle based on current crouching state
        const idleAnim = playerGroup.userData.isCrouching ? 'crouchIdle' : 'idle';
        console.log(`Player ${playerGroup.userData.playerId} stopped moving for 1s, forcing idle`);
        this.playPlayerAnimation(playerGroup, idleAnim);
      }
    }
    
    // Ensure player is above terrain after interpolation
    if (this.mapScene && typeof this.mapScene.getTerrainHeightAt === 'function') {
      const terrainHeight = this.mapScene.getTerrainHeightAt(
        playerGroup.position.x, 
        playerGroup.position.z
      );
      if (terrainHeight !== undefined) {
        playerGroup.position.y = Math.max(
          playerGroup.position.y, 
          terrainHeight + TERRAIN_Y_OFFSET
        );
      }
    }
    
    // Update last position for animation calculations
    if (playerGroup.userData) {
      if (!playerGroup.userData.lastPosition) {
        playerGroup.userData.lastPosition = playerGroup.position.clone();
      } else {
        // Only update lastPosition if we've moved significantly
        const moveAmount = playerGroup.position.distanceTo(playerGroup.userData.lastPosition);
        if (moveAmount > 0.01) {
          playerGroup.userData.lastPosition.copy(playerGroup.position);
        }
      }
    }
  }
  
  // Helper to determine if a player is moving
  isPlayerMoving(playerGroup) {
    if (!playerGroup || !playerGroup.userData) return false;
    
    const playerId = playerGroup.userData.playerId;
    if (!playerId) return false;

    // Initialize movement state if needed
    if (!this.movementStates[playerId]) {
      this.movementStates[playerId] = {
        isMoving: false,
        lastPosition: playerGroup.position.clone(),
        lastMoveTime: 0
      };
    }

    const state = this.movementStates[playerId];
    const currentPos = playerGroup.position;
    
    // Calculate actual movement distance
    const dx = currentPos.x - state.lastPosition.x;
    const dz = currentPos.z - state.lastPosition.z;
    const distance = Math.sqrt(dx*dx + dz*dz);

    const now = Date.now();
    
    // If we detect movement above threshold
    if (distance > this.movementThreshold) {
      state.isMoving = true;
      state.lastMoveTime = now;
      state.lastPosition.copy(currentPos);
    } else if (state.isMoving) {
      // Only set to not moving after grace period
      const timeSinceLastMove = now - state.lastMoveTime;
      if (timeSinceLastMove > this.movementGracePeriod) {
        state.isMoving = false;
      }
    }

    return state.isMoving;
  }
  
  // Toggle hitbox visualization
  toggleHitboxes() {
    this.showHitboxes = !this.showHitboxes;
    console.log("Hitbox visualization " + (this.showHitboxes ? "enabled" : "disabled"));
    
    if (this.showHitboxes) {
      this.updateHitboxVisualizations();
    } else {
      this.clearHitboxVisualizations();
    }
    
    return this.showHitboxes;
  }
  
  // Method to update player spawn protection status and visual
  updateSpawnProtectionVisual(isProtected, playerId = null) {
    // For local player (first person view)
    if (!playerId && isProtected) {
      // Add a shield effect or overlay on the screen
      if (!this.spawnProtectionOverlay) {
        // Create a transparent blue overlay
        const overlayGeometry = new THREE.PlaneGeometry(2, 2);
        const overlayMaterial = new THREE.MeshBasicMaterial({
          color: 0x0088ff,
          transparent: true,
          opacity: 0.2,
          side: THREE.DoubleSide,
          depthTest: false
        });
        
        this.spawnProtectionOverlay = new THREE.Mesh(overlayGeometry, overlayMaterial);
        this.spawnProtectionOverlay.position.z = -1;
        
        // Add to the camera so it moves with the view
        if (this.camera) {
          this.camera.add(this.spawnProtectionOverlay);
        }
      }
    } else if (!playerId && !isProtected) {
      // Remove the shield effect for local player
      if (this.spawnProtectionOverlay) {
        if (this.camera) {
          this.camera.remove(this.spawnProtectionOverlay);
        }
        this.spawnProtectionOverlay = null;
      }
    } else if (playerId) {
      // Handle other players' protection effect
      const player = this.players[playerId];
      if (player) {
        this.addSpawnProtectionEffect(player, isProtected);
      }
    }
    
    // Ensure player is in idle animation when spawn protection is applied
    if (isProtected) {
      const player = playerId ? this.players[playerId] : this.localPlayer;
      if (player) {
        this.playPlayerAnimation(player, 'idle');
      }
    }
  }
  
  // Helper method to add spawn protection visual effect to a player
  addSpawnProtectionEffect(playerMesh, isProtected) {
    // Add or remove protection effect
    if (playerMesh) {
      // Remove any existing protection effect first
      if (playerMesh.userData.protectionEffect) {
        playerMesh.remove(playerMesh.userData.protectionEffect);
        playerMesh.userData.protectionEffect = null;
      }
      
      if (isProtected) {
        // Create protection aura
        const auraGeometry = new THREE.SphereGeometry(1.2, 16, 16);
        const auraMaterial = new THREE.MeshBasicMaterial({
          color: 0x0088ff,
          transparent: true,
          opacity: 0.3,
          side: THREE.DoubleSide
        });
        
        const aura = new THREE.Mesh(auraGeometry, auraMaterial);
        aura.scale.set(1, 1.2, 1); // Slightly elongated to match player height
        
        playerMesh.add(aura);
        playerMesh.userData.protectionEffect = aura;
        playerMesh.userData.isSpawnProtected = true;
        
        // Set a timeout to remove the effect after 3 seconds
        setTimeout(() => {
          if (playerMesh && playerMesh.userData && playerMesh.userData.protectionEffect) {
            playerMesh.remove(playerMesh.userData.protectionEffect);
            playerMesh.userData.protectionEffect = null;
            playerMesh.userData.isSpawnProtected = false;
          }
        }, 3000);
      } else {
        playerMesh.userData.isSpawnProtected = false;
      }
    }
  }
  
  // Create damage direction indicator for local player
  showDamageDirectionIndicator(attackerPosition) {
    if (!this.localPlayer || !attackerPosition) return;
    
    // Create a 2D arrow sprite that points toward the attacker
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = 64;
    canvas.height = 64;
    
    // Draw a red arrow
    context.fillStyle = "#ff0000";
    context.beginPath();
    context.moveTo(32, 10);
    context.lineTo(52, 40);
    context.lineTo(40, 40);
    context.lineTo(40, 54);
    context.lineTo(24, 54);
    context.lineTo(24, 40);
    context.lineTo(12, 40);
    context.closePath();
    context.fill();
    
    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      opacity: 0.7
    });
    
    const sprite = new THREE.Sprite(material);
    sprite.scale.set(0.5, 0.5, 1);
    
    // Position above the player
    sprite.position.copy(this.localPlayer.position);
    sprite.position.y += 2.0;
    this.scene.add(sprite);
    
    // Calculate direction toward attacker
    const direction = new THREE.Vector3();
    direction.subVectors(
      new THREE.Vector3(attackerPosition.x, 0, attackerPosition.z),
      new THREE.Vector3(this.localPlayer.position.x, 0, this.localPlayer.position.z)
    ).normalize();
    
    // Calculate rotation to face attacker (on XZ plane)
    const angle = Math.atan2(direction.x, direction.z);
    sprite.material.rotation = angle;
    
    // Fade out after a short time
    let opacity = 0.7;
    const fadeOut = () => {
      opacity -= 0.05;
      material.opacity = opacity;
      
      if (opacity > 0) {
        requestAnimationFrame(fadeOut);
      } else {
        this.scene.remove(sprite);
        texture.dispose();
        material.dispose();
      }
    };
    
    // Start fade out after 1 second
    setTimeout(() => {
      requestAnimationFrame(fadeOut);
    }, 1000);
  }
  
  // Create hit effect at impact point
  createHitEffect(position, bodyPart = 'default') {
    // Different colors for different body parts
    const hitColors = {
      head: 0xff0000,    // Red for headshots
      torso: 0xffff00,   // Yellow for torso
      limb: 0x00ff00,    // Green for limbs
      hand: 0x0000ff,    // Blue for extremities
      default: 0xff0000  // Red for other hits
    };
    
    // Get color based on body part
    const hitColor = hitColors[bodyPart] || hitColors.default;
    
    // Create a simple particle effect at the hit position
    const hitEffect = new THREE.Mesh(
      new THREE.SphereGeometry(0.1, 8, 8),
      new THREE.MeshBasicMaterial({ 
        color: hitColor,
        transparent: true,
        opacity: 0.8 
      })
    );
    
    // Position the hit effect
    hitEffect.position.copy(position);
    
    // Scale based on body part
    if (bodyPart === 'head') {
      hitEffect.scale.multiplyScalar(1.5);
    }
    
    // Add to scene
    this.scene.add(hitEffect);
    
    // Remove after a short delay
    setTimeout(() => {
      this.scene.remove(hitEffect);
      hitEffect.material.dispose();
      hitEffect.geometry.dispose();
    }, 200);
  }
  
  // Create special effect for headshots
  createHeadshotEffect(position) {
    // Create a larger, pulsing red effect for headshots
    const geometry = new THREE.SphereGeometry(0.2, 16, 16);
    const material = new THREE.MeshBasicMaterial({
      color: 0xff0000,
      transparent: true,
      opacity: 0.8
    });
    
    const effect = new THREE.Mesh(geometry, material);
    effect.position.copy(position);
    this.scene.add(effect);
    
    // Animate the effect - make it pulse and fade out
    let scale = 1;
    let expanding = true;
    let opacity = 0.8;
    
    const animate = () => {
      // Pulse effect
      if (expanding) {
        scale += 0.05;
        if (scale >= 1.5) expanding = false;
      } else {
        scale -= 0.05;
        if (scale <= 0.8) expanding = true;
      }
      
      // Apply scale
      effect.scale.set(scale, scale, scale);
      
      // Fade out
      opacity -= 0.02;
      if (material.opacity > 0) {
        material.opacity = opacity;
      }
      
      // Continue animation until fully faded
      if (opacity > 0) {
        requestAnimationFrame(animate);
      } else {
        // Clean up
        this.scene.remove(effect);
        geometry.dispose();
        material.dispose();
      }
    };
    
    // Start animation
    requestAnimationFrame(animate);
  }
  
  // Create visual effect for melee attack
  createMeleeAttackVisual(playerMesh) {
    if (!playerMesh || !playerMesh.material) return;
    
    // Simple animation - flash the player mesh
    const originalMaterials = [];
    
    // Store original materials
    playerMesh.traverse(child => {
      if (child.isMesh && child.material) {
        if (Array.isArray(child.material)) {
          child.material.forEach(mat => {
            originalMaterials.push({
              mesh: child,
              material: mat,
              originalColor: mat.color.clone()
            });
            mat.color.set(0xffff00); // Yellow flash
          });
        } else {
          originalMaterials.push({
            mesh: child,
            material: child.material,
            originalColor: child.material.color.clone()
          });
          child.material.color.set(0xffff00); // Yellow flash
        }
      }
    });
    
    // Restore original colors after a short delay
    setTimeout(() => {
      originalMaterials.forEach(item => {
        item.material.color.copy(item.originalColor);
      });
    }, 100);
  }
  
  // Create gun shot visual effect
  createGunShotVisual(playerMesh, position, direction) {
    // Create a simple muzzle flash effect
    const muzzleFlash = new THREE.Mesh(
      new THREE.SphereGeometry(0.1, 8, 8),
      new THREE.MeshBasicMaterial({ color: 0xffff00 }) // Yellow flash
    );
    
    // We'll use the player's actual position for the muzzle flash origin
    // This ensures the visual appears correctly even if the network position data is slightly off
    muzzleFlash.position.copy(playerMesh.position);
    
    // Adjust height to approximate gun position (roughly at chest/shoulder height)
    muzzleFlash.position.y += 0.8;
    
    // Adjust the position slightly forward in the direction of fire
    const dirVector = direction.clone().normalize();
    muzzleFlash.position.add(dirVector.multiplyScalar(0.5));
    
    // Add to scene
    this.scene.add(muzzleFlash);
    
    // Remove after a short delay
    setTimeout(() => {
      this.scene.remove(muzzleFlash);
      muzzleFlash.material.dispose();
      muzzleFlash.geometry.dispose();
    }, 100);
  }
  
  // Create gun shot visual at a specific position (without needing a player mesh)
  createGunShotVisualAtPosition(position, direction) {
    // Create a simple muzzle flash effect
    const muzzleFlash = new THREE.Mesh(
      new THREE.SphereGeometry(0.0015, 8, 8), // Slightly larger to be more visible
      new THREE.MeshBasicMaterial({ color: 0xffff00 }) // Yellow flash
    );
    
    // Position the muzzle flash
    muzzleFlash.position.copy(position);
    
    // Make sure the flash is visible by adjusting height if needed
    if (muzzleFlash.position.y < 0.5) {
      muzzleFlash.position.y += 0.8; // Approximate chest height
    }
    
    // Adjust the position slightly forward in the direction of fire
    const dirVector = direction.clone().normalize();
    muzzleFlash.position.add(dirVector.multiplyScalar(0.5));
    
    // Add to scene
    this.scene.add(muzzleFlash);
    
    // Make the flash a bit more noticeable with a quick scaling animation
    const originalScale = muzzleFlash.scale.clone();
    muzzleFlash.scale.multiplyScalar(1.5);
    
    setTimeout(() => {
      muzzleFlash.scale.copy(originalScale);
    }, 50);
    
    // Remove after a short delay
    setTimeout(() => {
      this.scene.remove(muzzleFlash);
      muzzleFlash.material.dispose();
      muzzleFlash.geometry.dispose();
    }, 150); // Slightly longer to ensure visibility
  }
  
  // Create respawn effect at a specific position
  createRespawnEffect(position) {
    if (!this.scene) return;
    
    // Create a particle effect for respawn
    const geometry = new THREE.SphereGeometry(0.5, 16, 16);
    const material = new THREE.MeshBasicMaterial({
      color: 0x00ffff, // Cyan color for respawn effect
      transparent: true,
      opacity: 0.8
    });
    
    const respawnEffect = new THREE.Mesh(geometry, material);
    respawnEffect.position.copy(position);
    this.scene.add(respawnEffect);
    
    // Animate the respawn effect
    const startScale = 0.1;
    const endScale = 2.0;
    const duration = 1000; // 1 second
    const startTime = Date.now();
    
    // Scale and fade animation
    const animateRespawn = () => {
      const elapsedTime = Date.now() - startTime;
      const progress = Math.min(elapsedTime / duration, 1.0);
      
      // Scale up
      const scale = startScale + (endScale - startScale) * progress;
      respawnEffect.scale.set(scale, scale, scale);
      
      // Fade out
      material.opacity = 0.8 * (1 - progress);
      
      if (progress < 1) {
        requestAnimationFrame(animateRespawn);
      } else {
        // Remove the effect when animation is complete
        this.scene.remove(respawnEffect);
        geometry.dispose();
        material.dispose();
      }
    };
    
    // Start animation
    requestAnimationFrame(animateRespawn);
    
    // Reset animation for the player at this position
    if (this.localPlayer && this.localPlayer.position.distanceTo(position) < 0.5) {
      this.playPlayerAnimation(this.localPlayer, 'idle');
    } else {
      // Find other player near this position
      Object.values(this.players).forEach(player => {
        if (player.position.distanceTo(position) < 0.5) {
          this.playPlayerAnimation(player, 'idle');
        }
      });
    }
  }
  
  // Get character dimensions for hitbox calculations
  getCharacterDimensions(characterType) {
    const characterConfig = this.characterModels[characterType] || this.characterModels.soldier;
    const characterScale = characterConfig.scale;
    
    return {
      width: 0.5 * characterScale,
      depth: 0.4 * characterScale,
      height: characterConfig.height
    };
  }
  
  // Setter methods to update references if not available during construction
  setScene(scene) {
    this.scene = scene;
    // If we already created players but didn't have a scene, add them now
    if (this.scene) {
      if (this.localPlayer && !this.localPlayer.parent) {
        this.scene.add(this.localPlayer);
      }
      Object.values(this.players).forEach(player => {
        if (!player.parent) {
          this.scene.add(player);
        }
      });
    }
  }
  
  setCamera(camera) {
    this.camera = camera;
  }
  
  setMapScene(mapScene) {
    this.mapScene = mapScene;
  }
  
  // Clean up resources when no longer needed
  dispose() {
    console.log('Disposing PlayerManager resources');
    
    // Remove all players
    if (this.localPlayer) {
      this.removePlayer(this.localPlayer.userData?.playerId || 'local-player');
    }
    
    Object.keys(this.players).forEach(playerId => {
      this.removePlayer(playerId);
    });
    
    // Dispose all player pools
    Object.values(this.playerPools).forEach(pool => {
      if (pool && typeof pool.dispose === 'function') {
        pool.dispose();
      }
    });
    
    // Clear model cache
    this.modelCache = {};
    
    // Clear hitbox helpers
    this.clearHitboxVisualizations();
    
    // Clean up any spawn protection effects
    if (this.spawnProtectionOverlay) {
      if (this.camera) {
        this.camera.remove(this.spawnProtectionOverlay);
      }
      this.spawnProtectionOverlay = null;
    }
    
    if (this.spawnProtectionTimer) {
      clearTimeout(this.spawnProtectionTimer);
      this.spawnProtectionTimer = null;
    }
  }
  
  // Attach a weapon model to a player
  attachWeaponToPlayer(playerGroup, weaponType) {
    if (playerGroup.userData.isLocalPlayer) {
      return null;
    }
    
    if (!playerGroup || !playerGroup.userData) {
      console.warn('Cannot attach weapon: player group or userData is invalid');
      return null;
    }
    
    // Get weapon model
    if (!this.weaponModels || !this.weaponModels[weaponType]) {
      console.error(`Weapon model ${weaponType} not found`);
      return null;
    }
    
    // Remove any existing weapon
    if (playerGroup.userData.weaponModel) {
      try {
        if (playerGroup.userData.weaponModel.parent) {
          playerGroup.userData.weaponModel.parent.remove(playerGroup.userData.weaponModel);
        }
      } catch (e) {
        console.error('Error removing existing weapon:', e);
      }
    }
    
    // Get the weapon model
    const weaponModel = this.weaponModels[weaponType];
    
    // Clone the weapon model
    const weaponGltf = this.deepCloneGltf(weaponModel);
    const weapon = weaponGltf.scene;
    
    // Create a weapon container
    const weaponContainer = new THREE.Group();
    
    // Scale the weapon appropriately
    const scale = 0.9;
    weapon.scale.set(scale, scale, scale);
    weapon.rotation.set(0, -Math.PI, 0);
    
    // Make sure all meshes are visible with proper materials
    weapon.traverse(obj => {
      obj.visible = true;
      if (obj.isMesh) {
        if (Array.isArray(obj.material)) {
          obj.material.forEach(mat => {
            mat.transparent = false;
            mat.opacity = 1.0;
            mat.alphaTest = 0.5;
            mat.needsUpdate = true;
          });
        } else {
          obj.material.transparent = false;
          obj.material.opacity = 1.0; 
          obj.material.alphaTest = 0.5;
          obj.material.needsUpdate = true;
        }
      }
    });
    
    // Add the weapon to our container
    weaponContainer.add(weapon);
    
    // Force update matrices for the entire player hierarchy
    playerGroup.updateMatrixWorld(true);
    playerGroup.children.forEach(child => child.updateMatrixWorld(true));
    
    // Find right hand bone
    let rightHandBone = null;
    
    if (playerGroup.userData.bones) {
      for (const boneName of Object.keys(playerGroup.userData.bones)) {
        if (boneName.toLowerCase().includes('right') && 
            boneName.toLowerCase().includes('hand')) {
          rightHandBone = playerGroup.userData.bones[boneName];
          break;
        }
      }
    }
    
    if (rightHandBone) {
      // Update bone matrix
      rightHandBone.updateWorldMatrix(true, false);
      
      // Add to scene with matrix transform (method that works for test clients)
      this.scene.add(weaponContainer);
      
      // Store references for update function
      weaponContainer.userData.referenceBone = rightHandBone;
      weaponContainer.userData.referencePlayer = playerGroup;
      
      // Position the weapon initially
      weapon.position.set(0, 0, -0.15);
      
      // Setup update function to follow bone - this is the key function that works for test clients
      const updateWeaponPosition = () => {
        if (!weaponContainer.userData.referenceBone || !weaponContainer.userData.referencePlayer) return;
        
        const bone = weaponContainer.userData.referenceBone;
        const player = weaponContainer.userData.referencePlayer;
        
        // Skip if player is no longer in scene
        if (!player.parent) {
          if (weaponContainer.parent) {
            weaponContainer.parent.remove(weaponContainer);
          }
          return;
        }
        
        // Force update matrices
        player.updateMatrixWorld(true);
        bone.updateWorldMatrix(true, false);
        
        // Get bone position and rotation
        const boneWorldPos = new THREE.Vector3();
        const boneWorldQuat = new THREE.Quaternion();
        const boneWorldScale = new THREE.Vector3();
        
        bone.matrixWorld.decompose(boneWorldPos, boneWorldQuat, boneWorldScale);
        
        // Apply to weapon container
        weaponContainer.position.copy(boneWorldPos);
        weaponContainer.quaternion.copy(boneWorldQuat);
        
        // Add offsets
        weaponContainer.position.x += 0.05;
        weaponContainer.position.z -= 0.05;
        
        // Apply rotation
        const additionalRotation = new THREE.Euler(Math.PI*0.47, Math.PI*0.1, -Math.PI*0.5);
        const additionalQuaternion = new THREE.Quaternion().setFromEuler(additionalRotation);
        weaponContainer.quaternion.multiply(additionalQuaternion);
        
        // Fine-tune position in local space
        weaponContainer.translateX(-0.10);
        weaponContainer.translateZ(0.01);
      };
      
      // Initial position update
      updateWeaponPosition();
      
      // Store the update function
      weaponContainer.userData.updatePosition = updateWeaponPosition;
      
      // Add to global array for animation loop updates
      if (!this.weaponUpdateFunctions) {
        this.weaponUpdateFunctions = [];
      }
      this.weaponUpdateFunctions.push(updateWeaponPosition);
      
      // Setup animation frame loop
      const animateFunction = () => {
        if (weaponContainer.userData.updatePosition) {
          weaponContainer.userData.updatePosition();
        }
        
        // Continue only if still in scene
        if (weaponContainer.parent) {
          requestAnimationFrame(animateFunction);
        }
      };
      animateFunction();
      
      // Store reference
      playerGroup.userData.weaponModel = weaponContainer;
      
      return weaponContainer;
    } else {
      // Fallback if no hand bone
      console.log('Using fallback weapon attachment (no hand bone found)');
      playerGroup.add(weaponContainer);
      weaponContainer.position.set(0.4, 0.7, 0.6);
      weapon.rotation.set(0, -Math.PI * 0.7, 0.2);
      
      // Store reference
      playerGroup.userData.weaponModel = weaponContainer;
      
      return weaponContainer;
    }
  }
  
  // Add a method to adjust the interpolation factor
  setInterpolationFactor(factor) {
    if (typeof factor === 'number' && factor >= 0 && factor <= 1) {
      console.log(`Updating interpolation factor: ${this.interpolationFactor} -> ${factor}`);
      this.interpolationFactor = factor;
      return true;
    } else {
      console.warn('Invalid interpolation factor: must be a number between 0 and 1');
      return false;
    }
  }
  
  // Add a new method to handle the animation weight blending
  updateAnimationBlending(playerGroup, deltaTime) {
    // Do nothing since animation blending is disabled
    return;
  }
  
  // Add a method to set animation blend durations
  setAnimationBlendDurations(options) {
    if (this.animationController) {
      this.animationController.setBlendDurations(options);
      console.log('Updated animation blend durations:', options);
      return true;
    }
    return false;
  }
  
  /**
   * Set animation configuration parameters
   * @param {Object} config - Configuration object for animations
   * @returns {boolean} - Whether the configuration was successfully applied
   */
  setAnimationConfig(config) {
    if (!this.animationController) {
      console.error('Cannot set animation config: Animation controller not initialized');
      return false;
    }
    
    try {
      const result = this.animationController.setAnimationConfig(config);
      console.log('Animation configuration updated:', config);
      return result;
    } catch (error) {
      console.error('Error setting animation config:', error);
      return false;
    }
  }
  
  /**
   * Get current animation configuration
   * @returns {Object} Current animation configuration
   */
  getAnimationConfig() {
    if (!this.animationController) return null;
    
    return {
      blendDurations: { ...this.animationController.blendDurations },
      lockTimeFactor: this.animationController.lockTimeFactor,
      movementThreshold: this.animationController.movementThreshold,
      debug: this.animationController.debug
    };
  }
  
  // Add an enhanced method to check for actual movement vs animation state
  checkAndFixPlayerAnimation(playerGroup) {
    if (!playerGroup || !playerGroup.userData || !playerGroup.userData.animations) return;
    
    // Skip if player is dead
    if (this.isPlayerDead(playerGroup)) {
      // Make sure we're not trying to play a non-death animation on a dead player
      const currentAnim = playerGroup.userData.currentAnimation;
      if (currentAnim && !currentAnim.includes('death')) {
        console.log(`Dead player ${playerGroup.userData.playerId} has non-death animation ${currentAnim}, fixing...`);
        this.handlePlayerDeath(playerGroup);
      }
      return;
    }

    const playerId = playerGroup.userData.playerId;
    if (!playerId) return;

    // Skip if we're in the middle of a blend
    if (this.animationTransitions[playerId]) return;

    const currentAnim = playerGroup.userData.currentAnimation;
    const isActuallyMoving = this.isPlayerMoving(playerGroup);
    const isMovementAnimation = this.isMovementAnimation(currentAnim);
    const state = this.movementStates[playerId];

    // Don't do anything if we don't have a movement state
    if (!state) return;

    // Handle animation state changes
    if (!isActuallyMoving && isMovementAnimation) {
      // Only set up idle transition if we don't already have one pending
      if (!state.pendingIdleTimeout) {
        state.pendingIdleTimeout = setTimeout(() => {
          // Double check movement state before changing to idle
          if (!this.isPlayerMoving(playerGroup) && !this.isPlayerDead(playerGroup)) {
            const properIdleAnim = playerGroup.userData.isCrouching ? 'crouchIdle' : 'idle';
            this.playPlayerAnimation(playerGroup, properIdleAnim);
          }
          state.pendingIdleTimeout = null;
        }, this.movementGracePeriod);
      }
    } 
    // If we're moving but in an idle animation
    else if (isActuallyMoving && !isMovementAnimation && !currentAnim.includes('death')) {
      // Clear any pending idle transition
      if (state.pendingIdleTimeout) {
        clearTimeout(state.pendingIdleTimeout);
        state.pendingIdleTimeout = null;
      }

      // Only update if we should
      if (this.shouldUpdateAnimation(playerId, 'walk')) {
        const newAnim = playerGroup.userData.isCrouching ? 'crouchForward' : 'walk';
        this.playPlayerAnimation(playerGroup, newAnim);
      }
    }
  }
  
  // Add a method to toggle animation blending
  toggleAnimationBlending() {
    // Animation blending is now disabled permanently
    console.log("Animation blending is disabled in this version");
    this.useAnimationBlending = false;
    this.animationTransitions = {};
    return false;
  }
  
  // Method to set animation blending state
  setAnimationBlending(enabled) {
    // Animation blending is now disabled permanently
    console.log("Animation blending is disabled in this version");
    this.useAnimationBlending = false;
    this.animationTransitions = {};
    return false;
  }
  
  // Add this helper method
  shouldUpdateAnimation(playerId, newAnimation) {
    const now = Date.now();
    const lastChange = this.lastAnimationChange[playerId];
    
    // Increase debounce time significantly
    if (!lastChange || now - lastChange > this.animationDebounceTime) {
      // Don't update timestamp for idle animations unless it's been a long time
      if (newAnimation !== 'idle' || !lastChange || now - lastChange > 1000) {
        this.lastAnimationChange[playerId] = now;
      }
      return true;
    }
    
    // If we're in a transition, don't allow new changes
    if (this.animationTransitions[playerId]) {
      return false;
    }
    
    return false;
  }

  // Add helper method to check if an animation is a movement animation
  isMovementAnimation(animationName) {
    return [
      'walk',
      'run',
      'walkBackward',
      'walkLeft',
      'walkRight',
      'crouchForward',
      'crouchBackward',
      'crouchLeft',
      'crouchRight'
    ].includes(animationName);
  }

  // Add helper method to determine death animation
  getDeathAnimation(playerGroup) {
    if (!playerGroup || !playerGroup.userData) return 'rifle_idle_death';

    const currentAnim = playerGroup.userData.currentAnimation;
    
    // Check if player was in a movement animation when they died
    const wasMoving = ['walk', 'run', 'walkLeft', 'walkRight', 'walkBackward'].includes(currentAnim);
    
    // Use run death animation if player was moving, otherwise use idle death
    return wasMoving ? 'rifle_run_death' : 'rifle_idle_death';
  }

  // Update the handlePlayerDeath method to handle both local and remote players
  handlePlayerDeath(playerGroup) {
    if (!playerGroup || !playerGroup.userData) return;
    
    // Mark player as dead in userData
    playerGroup.userData.isDead = true;
    
    // Use the animation controller to force the death animation
    // This bypasses priority checks and ensures the animation plays
    this.animationController.forceAnimation(playerGroup, 'death');
    
    // Add player ID for better logging
    const playerId = playerGroup.userData.playerId || 'unknown';
    console.log(`Playing death animation for player ${playerId}`);
  }

  // Add method to check if a player is dead
  isPlayerDead(playerGroup) {
    if (!playerGroup || !playerGroup.userData) return false;
    
    // Check the explicit isDead flag
    if (playerGroup.userData.isDead === true) {
      return true;
    }
    
    // Also check if the current animation is a death animation
    const currentAnim = playerGroup.userData.currentAnimation;
    if (currentAnim && (
        currentAnim === 'rifle_run_death' || 
        currentAnim === 'rifle_idle_death' || 
        currentAnim === 'death')
    ) {
      return true;
    }
    
    return false;
  }
  
  // Add method to set player team color
  setPlayerTeamColor(playerGroup, colorValue) {
    if (!playerGroup || !playerGroup.children || playerGroup.children.length === 0) {
      return;
    }
    
    // Find the model in the group
    const modelScene = playerGroup.children[0];
    if (!modelScene) return;
    
    // Apply color to materials
    modelScene.traverse(object => {
      if (object.isMesh && object.material) {
        // If it's an array of materials
        if (Array.isArray(object.material)) {
          object.material.forEach(material => {
            if (material.name && material.name.includes('team_color')) {
              material.color.setHex(colorValue);
            }
          });
        } 
        // If it's a single material
        else if (object.material.name && object.material.name.includes('team_color')) {
          object.material.color.setHex(colorValue);
        }
      }
    });
  }
  
  // Add a safe logging function to handle objects with circular references
  safeLogObject(obj) {
    // Create a simplified version of the object for logging
    const simplifiedObj = {};
    
    if (!obj) return 'null or undefined';
    
    // Handle special cases
    if (obj.constructor && obj.constructor.name === 'AnimationMixer') {
      return '[AnimationMixer object]';
    }
    
    if (obj.animations) {
      simplifiedObj.animations = Object.keys(obj.animations);
    }
    
    // Add other safe properties
    if (obj.poolIndex !== undefined) simplifiedObj.poolIndex = obj.poolIndex;
    if (obj.isPooled !== undefined) simplifiedObj.isPooled = obj.isPooled;
    if (obj.isActive !== undefined) simplifiedObj.isActive = obj.isActive;
    if (obj.character) simplifiedObj.character = obj.character;
    if (obj.playerId) simplifiedObj.playerId = obj.playerId;
    
    return JSON.stringify(simplifiedObj, null, 2);
  }

  // Add this method to limit the number of visible players for performance
  setMaxVisiblePlayers(maxPlayers) {
    this.maxVisiblePlayers = maxPlayers;
    console.log(`PlayerManager: Set max visible players to ${maxPlayers}`);
    
    // Store this value for later use in visibility culling
    if (!this.userData) this.userData = {};
    this.userData.maxVisiblePlayers = maxPlayers;
    
    return true;
  }
}

export default PlayerManager; 